import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Typography, TypographyVariant } from "../../../components/ui-kit";
import api from "../../../util/api";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  font-size: 14px;
  min-height: 60px;
  resize: vertical;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: "approve" | "reject" | "cancel" }>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  ${({ variant }) =>
    variant === "approve"
      ? "background: #16a34a; color: #fff;"
      : variant === "reject"
      ? "background: #dc2626; color: #fff;"
      : "background: #f1f5f9; color: #374151;"}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
`;

interface Props {
  approval: {
    _id: string;
    status: string;
    approvedProviders?: string[] | null;
    defaultProvider?: string | null;
    riskAssessment: string | null;
    rejectionReason?: string | null;
    adminNotes: string | null;
    company?: { _id: string; tradingName?: string; businessName?: string };
  };
  onClose: () => void;
  onDecided: () => void;
}

export const DecideApprovalModal: React.FC<Props> = ({
  approval,
  onClose,
  onDecided,
}) => {
  const [providers, setProviders] = useState<string[]>(
    approval.approvedProviders ?? []
  );
  const [defaultProvider, setDefaultProvider] = useState(
    approval.defaultProvider || ""
  );
  const [riskAssessment, setRiskAssessment] = useState(
    approval.riskAssessment || ""
  );
  // Reject is a two-step action: the reason box only exists while confirming a
  // rejection, so a reason can't be entered alongside an approval.
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [adminNotes, setAdminNotes] = useState(approval.adminNotes || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [onboardingStatus, setOnboardingStatus] = useState<{
    ryftOnboarded?: boolean;
    ryftVerificationStatus?: string | null;
    ryftCardPaymentsEnabled?: boolean;
    niftipayOnboarded?: boolean;
    niftipayVerificationStatus?: string | null;
  } | null>(null);
  const [decision, setDecision] = useState<{
    decidedBy?: { firstName?: string; lastName?: string; email?: string };
    decidedAt?: string;
  } | null>(null);

  useEffect(() => {
    api
      .getCardPaymentApprovalById(approval._id)
      .then((detail: any) => {
        if (detail?.onboardingStatus)
          setOnboardingStatus(detail.onboardingStatus);
        if (detail?.decidedAt)
          setDecision({
            decidedBy: detail.decidedBy,
            decidedAt: detail.decidedAt,
          });
      })
      .catch(() => undefined);
  }, [approval._id]);

  const isHighRisk = riskAssessment === "HIGH";
  // The backend refuses this: approval only gates creating the Ryft account,
  // so once it exists, card payments are stopped with the Ryft tab toggle.
  const cannotReject =
    approval.status === "APPROVED" && !!onboardingStatus?.ryftOnboarded;

  const hasReason = rejectionReason.trim().length > 0;
  const riskUnchanged = riskAssessment === (approval.riskAssessment || "");
  const notesUnchanged = adminNotes === (approval.adminNotes || "");
  const storedProviders = approval.approvedProviders ?? [];
  // Nothing to send when the decision would save what is already stored.
  const approveUnchanged =
    approval.status === "APPROVED" &&
    riskUnchanged &&
    notesUnchanged &&
    providers.length === storedProviders.length &&
    providers.every((p) => storedProviders.includes(p)) &&
    (defaultProvider || providers[0]) ===
      (approval.defaultProvider || storedProviders[0]);
  const rejectUnchanged =
    approval.status === "REJECTED" &&
    riskUnchanged &&
    notesUnchanged &&
    rejectionReason.trim() === (approval.rejectionReason || "");

  const startRejecting = () => {
    setError("");
    setRejectionReason(approval.rejectionReason || "");
    setRejecting(true);
  };

  const hint =
    (!rejecting && approveUnchanged) || (rejecting && rejectUnchanged)
      ? "No changes to save."
      : !rejecting && providers.length === 0
      ? "Select at least one provider to approve."
      : rejecting && !hasReason
      ? "Enter a reason to reject. The merchant will see it."
      : "";

  const toggleProvider = (value: string) => {
    const next = providers.includes(value)
      ? providers.filter((p) => p !== value)
      : [...providers, value];
    setProviders(next);
    // Keep the default pointing at something still ticked.
    if (!next.includes(defaultProvider)) {
      setDefaultProvider(next[0] ?? "");
    }
  };

  const handleDecide = async (decision: "approve" | "reject") => {
    setError("");
    setSubmitting(true);
    try {
      await api.decideCardPaymentApproval(approval._id, {
        decision,
        ...(decision === "approve"
          ? {
              providers,
              defaultProvider: defaultProvider || providers[0],
            }
          : {}),
        ...(riskAssessment ? { riskAssessment } : {}),
        ...(decision === "reject"
          ? { rejectionReason: rejectionReason.trim() }
          : {}),
        // Always sent, so clearing the box clears the saved note.
        adminNotes,
      });
      onDecided();
    } catch (e: any) {
      setError(e?.message || "Failed to submit decision");
    } finally {
      setSubmitting(false);
    }
  };

  const companyName =
    approval.company?.tradingName ||
    approval.company?.businessName ||
    "Unknown";

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Typography
          variant={TypographyVariant.HEADER2}
          style={{ marginBottom: 16 }}
        >
          Card Payment Approval
        </Typography>

        <InfoRow>
          <strong>Company:</strong> {companyName}
        </InfoRow>
        <InfoRow>
          <strong>Status:</strong> {approval.status}
        </InfoRow>
        {decision && (
          <InfoRow>
            <strong>Decided:</strong>{" "}
            {new Date(decision.decidedAt as string).toLocaleString("en-GB")}
            {decision.decidedBy
              ? ` by ${
                  [decision.decidedBy.firstName, decision.decidedBy.lastName]
                    .filter(Boolean)
                    .join(" ") || decision.decidedBy.email
                }`
              : ""}
          </InfoRow>
        )}
        {approval.status === "REJECTED" && approval.rejectionReason && (
          <InfoRow>
            <strong>Rejection reason:</strong> {approval.rejectionReason}
          </InfoRow>
        )}
        {onboardingStatus && (
          <InfoRow>
            <strong>Ryft:</strong>{" "}
            {onboardingStatus.ryftOnboarded
              ? `Onboarded (${
                  onboardingStatus.ryftVerificationStatus ?? "unknown"
                })`
              : "Not onboarded"}
          </InfoRow>
        )}
        {onboardingStatus && (
          <InfoRow>
            <strong>Niftipay:</strong>{" "}
            {onboardingStatus.niftipayOnboarded
              ? `Onboarded (${
                  onboardingStatus.niftipayVerificationStatus ?? "unknown"
                })`
              : "Not onboarded"}
          </InfoRow>
        )}

        <Field>
          <Label>Risk Assessment (optional)</Label>
          <Select
            value={riskAssessment}
            onChange={(e) => {
              setRiskAssessment(e.target.value);
              if (e.target.value === "HIGH") {
                // Ryft is not permitted for high-risk merchants.
                setProviders((prev) => prev.filter((p) => p !== "ryft"));
                setDefaultProvider((current) =>
                  current === "ryft" ? "niftipay" : current
                );
              }
            }}
          >
            <option value="">Select risk level...</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </Select>
        </Field>

        {!rejecting && (
          <Field>
            <Label>Approved providers</Label>
            <CheckboxRow>
              <label>
                <input
                  type="checkbox"
                  checked={providers.includes("ryft")}
                  disabled={isHighRisk}
                  onChange={() => toggleProvider("ryft")}
                />
                Ryft{isHighRisk ? " (unavailable for high risk)" : ""}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={providers.includes("niftipay")}
                  onChange={() => toggleProvider("niftipay")}
                />
                Niftipay
              </label>
            </CheckboxRow>
          </Field>
        )}

        {!rejecting && providers.length > 1 && (
          <Field>
            <Label>Default provider</Label>
            <Select
              value={defaultProvider}
              onChange={(e) => setDefaultProvider(e.target.value)}
            >
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {rejecting && (
          <Field>
            <Label htmlFor="card-approval-rejection-reason">
              Rejection reason (required, visible to merchant)
            </Label>
            <TextArea
              id="card-approval-rejection-reason"
              autoFocus
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why card payments were not approved..."
            />
          </Field>
        )}

        <Field>
          <Label>Admin Notes (internal only)</Label>
          <TextArea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Internal notes..."
          />
        </Field>

        {cannotReject && (
          <Typography
            variant={TypographyVariant.BODY7}
            style={{ color: "#6b7280", marginBottom: 8 }}
          >
            This company already has a Ryft account, so it can&apos;t be
            rejected here. To stop card payments, turn off Ryft card payments in
            the company&apos;s Ryft tab.
          </Typography>
        )}

        {hint && (
          <Typography
            variant={TypographyVariant.BODY7}
            style={{ color: "#6b7280", marginBottom: 8 }}
          >
            {hint}
          </Typography>
        )}

        {error && (
          <Typography
            variant={TypographyVariant.BODY7}
            style={{ color: "#dc2626", marginBottom: 8 }}
          >
            {error}
          </Typography>
        )}

        {rejecting ? (
          <ButtonRow>
            <Button
              variant="cancel"
              disabled={submitting}
              onClick={() => {
                setError("");
                setRejecting(false);
              }}
            >
              Back
            </Button>
            <Button
              variant="reject"
              disabled={submitting || !hasReason || rejectUnchanged}
              onClick={() => handleDecide("reject")}
            >
              Confirm rejection
            </Button>
          </ButtonRow>
        ) : (
          <ButtonRow>
            <Button variant="cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="reject"
              disabled={submitting || cannotReject}
              onClick={startRejecting}
            >
              Reject...
            </Button>
            <Button
              variant="approve"
              disabled={
                submitting || providers.length === 0 || approveUnchanged
              }
              onClick={() => handleDecide("approve")}
            >
              {approval.status === "APPROVED" ? "Update approval" : "Approve"}
            </Button>
          </ButtonRow>
        )}
      </Modal>
    </Overlay>
  );
};
